import { useEffect, useState } from 'react';
import { Button, Icon, Sheet } from '@astral/ui';
import { MAX_STICKER_EMOJIS, type StickerAsset } from '@astral/shared';
import { useT } from '../providers/I18nProvider.js';
import { EmojiSequenceEditor } from './EmojiSequenceEditor.js';

export interface StickerActionSheetProps {
  asset: StickerAsset | null;
  onClose: () => void;
  /** Replace this sticker's emoji list (1–20 emoji). */
  onSetEmojis: (id: string, list: string[]) => void;
  onReplace: () => void;
  onDelete: () => void;
}

/** Compact per-sticker editor: preview, emoji list (1–20), replace, delete. */
export function StickerActionSheet({
  asset,
  onClose,
  onSetEmojis,
  onReplace,
  onDelete,
}: StickerActionSheetProps): JSX.Element {
  const t = useT();
  const [editingEmojis, setEditingEmojis] = useState(false);

  // Collapse the emoji editor whenever the sheet targets a different sticker.
  useEffect(() => {
    setEditingEmojis(false);
  }, [asset?.id]);

  return (
    <Sheet open={Boolean(asset)} onClose={onClose} title={t('stickers.preview')}>
      {asset && (
        <div className="sticker-action">
          <div className="sticker-action__preview">
            {asset.kind === 'video' ? (
              <video className="sticker-tile__img" src={asset.src} muted loop autoPlay playsInline />
            ) : (
              <img className="sticker-tile__img" src={asset.src} alt={asset.name} />
            )}
          </div>

          {editingEmojis ? (
            <div className="stack" style={{ gap: 8 }}>
              <span className="as-field__label">{t('stickers.emojiListLabel')}</span>
              <EmojiSequenceEditor
                emojis={asset.emojiList}
                max={MAX_STICKER_EMOJIS}
                onChange={(list) => onSetEmojis(asset.id, list)}
              />
              <Button variant="ghost" onClick={() => setEditingEmojis(false)}>
                {t('common.done')}
              </Button>
            </div>
          ) : (
            <button className="sticker-action__emoji border-glow" onClick={() => setEditingEmojis(true)}>
              <span className="sticker-action__emoji-glyph">{asset.emojiList.join(' ')}</span>
              <span className="sticker-action__emoji-label">{t('stickers.changeEmoji')}</span>
              <Icon name="chevron-right" size={18} />
            </button>
          )}

          <div className="stack">
            <Button fullWidth variant="secondary" leftIcon={<Icon name="upload" size={18} />} onClick={onReplace}>
              {t('stickers.replace')}
            </Button>
            <Button fullWidth variant="ghost" leftIcon={<Icon name="trash" size={18} />} onClick={onDelete}>
              {t('common.delete')}
            </Button>
          </div>
        </div>
      )}
    </Sheet>
  );
}
